import carbonApi from "@/submodules/carbon-plugin/api";

function unwrap(response, { defaultMessage } = {}) {
    if (!response) {
        throw new Error(defaultMessage || "응답이 존재하지 않습니다.");
    }

    const payload = response.data ?? response;

    if (payload?.success === false) {
        const message =
            payload?.error?.message || payload?.message || defaultMessage || "요청 처리 중 오류가 발생했습니다.";
        const error = new Error(message);
        if (payload?.error?.code) {
            error.code = payload.error.code;
        }
        error.payload = payload;
        throw error;
    }

    return payload?.data ?? payload ?? null;
}

export async function fetchOrganizations(params = {}) {
    const response = await carbonApi.organization.getOrgs(params.page, params.limit);
    const data = unwrap(response, { defaultMessage: "조직 목록을 불러오지 못했습니다." });

    if (Array.isArray(data?.organizations)) return data.organizations;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.orgs)) return data.orgs;
    if (Array.isArray(data?.data?.items)) return data.data.items;

    return [];
}

export async function fetchOrgBoundaries(orgCode, status) {
    if (!orgCode) {
        throw new Error("orgCode는 필수입니다.");
    }

    const response = await carbonApi.orgBoundary.getOrgBoundaries(orgCode, status);
    const data = unwrap(response, { defaultMessage: "조직 경계 목록을 불러오지 못했습니다." });

    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.boundaries)) return data.boundaries;

    return [];
}

export async function fetchOperBoundaries(orgCode) {
    if (!orgCode) {
        throw new Error("orgCode는 필수입니다.");
    }

    const response = await carbonApi.operBoundary.getOperBoundaries(orgCode);
    const data = unwrap(response, { defaultMessage: "운영 경계 목록을 불러오지 못했습니다." });

    if (Array.isArray(data?.boundaries)) return data.boundaries;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;

    return [];
}

export async function fetchInventories(params = {}) {
    const response = await carbonApi.inventory.getInventories(params);
    const data = unwrap(response, { defaultMessage: "인벤토리 목록을 불러오지 못했습니다." });

    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.inventories)) return data.inventories;
    if (Array.isArray(data?.data?.items)) return data.data.items;
    if (Array.isArray(data?.data?.inventories)) return data.data.inventories;

    return [];
}

export async function fetchInventoryDetail(inventoryIdx) {
    if (!inventoryIdx) {
        throw new Error("inventoryIdx는 필수입니다.");
    }

    const response = await carbonApi.inventory.getInventory(inventoryIdx);
    return unwrap(response, { defaultMessage: "인벤토리 정보를 불러오지 못했습니다." });
}

export async function fetchOrgBoundaryDetail(boundaryIdx) {
    if (!boundaryIdx) {
        throw new Error("orgBoundaryIdx는 필수입니다.");
    }

    const response = await carbonApi.orgBoundary.getOrgBoundary(boundaryIdx);
    return unwrap(response, { defaultMessage: "조직 경계 정보를 불러오지 못했습니다." });
}

export async function fetchOperBoundaryDetail(operBoundaryIdx) {
    if (!operBoundaryIdx) {
        throw new Error("operBoundaryIdx는 필수입니다.");
    }

    const response = await carbonApi.operBoundary.getOperBoundary(operBoundaryIdx);
    return unwrap(response, { defaultMessage: "운영 경계 정보를 불러오지 못했습니다." });
}

export async function fetchEmissionTargets(filters = {}) {
    const response = await carbonApi.target.getTargets(filters);
    const data = unwrap(response, { defaultMessage: "배출 목표 목록을 불러오지 못했습니다." });

    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.targets)) return data.targets;
    if (Array.isArray(data)) return data;

    return [];
}

export async function fetchEmissionTargetDetail(targetIdx) {
    if (!targetIdx) {
        throw new Error("targetIdx는 필수입니다.");
    }

    const response = await carbonApi.target.getTarget(targetIdx);
    return unwrap(response, { defaultMessage: "배출 목표 상세 정보를 불러오지 못했습니다." });
}

export async function fetchEmissionTargetPerformance(targetIdx, params = {}) {
    if (!targetIdx) {
        throw new Error("targetIdx는 필수입니다.");
    }

    const response = await carbonApi.target.getTargetPerformance(targetIdx, params);
    return unwrap(response, { defaultMessage: "배출 목표 성과 정보를 불러오지 못했습니다." });
}

export async function createEmissionTarget(payload) {
    const response = await carbonApi.target.createTarget(payload);
    return unwrap(response, { defaultMessage: "배출 목표 저장에 실패했습니다." });
}

export async function updateEmissionTarget(targetIdx, payload) {
    if (!targetIdx) {
        throw new Error("targetIdx는 필수입니다.");
    }

    const response = await carbonApi.target.updateTarget(targetIdx, payload);
    return unwrap(response, { defaultMessage: "배출 목표 수정에 실패했습니다." });
}

export async function deleteEmissionTarget(targetIdx) {
    if (!targetIdx) {
        throw new Error("targetIdx는 필수입니다.");
    }

    const response = await carbonApi.target.deleteTarget(targetIdx);
    const data = unwrap(response, { defaultMessage: "배출 목표 삭제에 실패했습니다." });

    return {
        success: data?.success ?? true,
        message: data?.message || "배출 목표가 삭제되었습니다.",
    };
}

export async function getAllEmissionTargets() {
    const response = await carbonApi.target.getTargets();
    const data = unwrap(response, { defaultMessage: "배출 목표 목록을 불러오지 못했습니다." });

    if (Array.isArray(data?.targets)) return data.targets;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data)) return data;

    return [];
}

export async function fetchBaselineEmission({ orgIdx, boundaryIdx, operBoundaryIdx, baseYear }) {
    if (!orgIdx || !baseYear) {
        throw new Error("orgIdx와 baseYear는 필수입니다.");
    }

    const params = {
        orgIdx,
        boundaryIdx,
        operBoundaryIdx,
        startYear: baseYear,
        endYear: baseYear,
        limit: 1,
        page: 1,
    };

    const response = await carbonApi.inventory.getInventories(params);
    const data = unwrap(response, { defaultMessage: "기준 배출량을 불러오지 못했습니다." });

    const inventories =
        data?.inventories ||
        data?.items ||
        data?.data?.inventories ||
        data?.data?.items ||
        (Array.isArray(data) ? data : []);

    const record = Array.isArray(inventories) ? inventories[0] : inventories;

    return {
        emission: record?.totalEmission ?? record?.totalEmissions ?? 0,
        inventory: record || null,
    };
}

export async function fetchBoundaryTrends({ boundaryIdx, operBoundaryIdx, periodFrom, periodTo, groupBy = "year" }) {
    if (!boundaryIdx || !operBoundaryIdx) {
        throw new Error("boundaryIdx와 operBoundaryIdx는 필수입니다.");
    }

    const response = await carbonApi.inventory.getInventoryTrends({
        boundaryIdx,
        operBoundaryIdx,
        periodFrom,
        periodTo,
        groupBy,
    });

    return unwrap(response, { defaultMessage: "배출 추이를 불러오지 못했습니다." });
}

export async function fetchTrendSeries({ boundaryIdx, operBoundaryIdx, periodFrom, periodTo, groupBy = "year" }) {
    const trendPayload = await fetchBoundaryTrends({ boundaryIdx, operBoundaryIdx, periodFrom, periodTo, groupBy });
    const trends = trendPayload?.trends || trendPayload || [];
    return buildTrendSeries(trends);
}

export function summarizeTrendTotals(trends = []) {
    return trends.map((item) => ({
        year: item.year,
        total:
            item.total ??
            item.totalEmission ??
            item.totalEmissions ??
            (item.scope1 ?? 0) + (item.scope2 ?? 0) + (item.scope3 ?? 0),
    }));
}

export function buildTrendSeries(trends = []) {
    if (!Array.isArray(trends)) return [];

    return trends.map((item) => {
        const year = item.year || item.period?.slice(0, 4);
        const scope1 = item.scope1Emission ?? item.scope1 ?? 0;
        const scope2 = item.scope2Emission ?? item.scope2 ?? 0;
        const scope3 = item.scope3Emission ?? item.scope3 ?? 0;
        const total = item.totalEmission ?? item.totalEmissions ?? scope1 + scope2 + scope3;

        return {
            year: year ? Number(year) : null,
            scope1,
            scope2,
            scope3,
            total,
        };
    });
}

export function computeLinearTargetSeries(target, trends = []) {
    if (!target) return [];

    const baseYear = target.baseYear;
    const targetYear = target.targetYear;
    if (!baseYear || !targetYear || targetYear <= baseYear) return [];

    const from = Math.min(baseYear, ...trends.map((row) => row.year || baseYear));
    const to = Math.max(targetYear, ...trends.map((row) => row.year || targetYear));
    const years = [];

    for (let year = from; year <= to; year += 1) {
        years.push(year);
    }

    const baseEmission = target.baseYearEmissions ?? target?.target?.baseYearEmissions ?? 0;
    const targetEmission = target.targetEmissions ?? target?.target?.targetEmissions ?? 0;
    const span = targetYear - baseYear;

    return years.map((year) => {
        if (year <= baseYear) return { year, planned: baseEmission };
        if (year >= targetYear) return { year, planned: targetEmission };

        const progress = (year - baseYear) / span;
        const planned = baseEmission - (baseEmission - targetEmission) * progress;
        return { year, planned: Number(planned.toFixed(2)) };
    });
}

export async function fetchPathways(targetIdx) {
    if (!targetIdx) {
        throw new Error("targetIdx는 필수입니다.");
    }

    const response = await carbonApi.reductionTarget.getPathways(targetIdx);
    const data = unwrap(response, { defaultMessage: "감축 경로 목록을 불러오지 못했습니다." });

    if (Array.isArray(data?.pathways)) return data.pathways;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;

    return [];
}

export async function createPathway(targetIdx, payload) {
    if (!targetIdx) {
        throw new Error("targetIdx는 필수입니다.");
    }

    const response = await carbonApi.reductionTarget.createPathway(targetIdx, payload);
    return unwrap(response, { defaultMessage: "감축 경로 저장에 실패했습니다." });
}

// ============================================
// 준수기준 관리 API
// ============================================

export async function fetchCompliances(params = {}) {
    const response = await carbonApi.compliance.getCompliances(params);
    const data = unwrap(response, { defaultMessage: "준수기준 목록을 불러오지 못했습니다." });

    if (Array.isArray(data?.complianceStandards)) return data.complianceStandards;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;

    return [];
}

export async function createCompliance(payload) {
    const response = await carbonApi.compliance.createCompliance(payload);
    const payload_data = response?.data ?? response;

    if (payload_data?.success === false) {
        const message = payload_data?.error?.message || payload_data?.message || "준수기준 저장에 실패했습니다.";
        const error = new Error(message);
        if (payload_data?.error?.code) {
            error.code = payload_data.error.code;
        }
        error.payload = payload_data;
        throw error;
    }

    return payload_data;
}

export async function updateCompliance(complianceIdx, payload) {
    if (!complianceIdx) {
        throw new Error("complianceIdx는 필수입니다.");
    }

    const response = await carbonApi.compliance.updateCompliance(complianceIdx, payload);
    const payload_data = response?.data ?? response;

    if (payload_data?.success === false) {
        const message = payload_data?.error?.message || payload_data?.message || "준수기준 수정에 실패했습니다.";
        const error = new Error(message);
        if (payload_data?.error?.code) {
            error.code = payload_data.error.code;
        }
        error.payload = payload_data;
        throw error;
    }

    return payload_data;
}

export async function deleteCompliance(complianceIdx) {
    if (!complianceIdx) {
        throw new Error("complianceIdx는 필수입니다.");
    }

    const response = await carbonApi.compliance.deleteCompliance(complianceIdx);
    const payload_data = response?.data ?? response;

    if (payload_data?.success === false) {
        const message = payload_data?.error?.message || payload_data?.message || "준수기준 삭제에 실패했습니다.";
        const error = new Error(message);
        if (payload_data?.error?.code) {
            error.code = payload_data.error.code;
        }
        error.payload = payload_data;
        throw error;
    }

    return payload_data;
}

// ============================================
// 목표(Goal) 관리 API
// ============================================

export async function fetchGoals(params = {}) {
    const response = await carbonApi.goal.getGoals(params);
    const data = unwrap(response, { defaultMessage: "목표 목록을 불러오지 못했습니다." });

    if (Array.isArray(data?.emissionTargets)) return data.emissionTargets;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.targets)) return data.targets;

    return [];
}

export async function createGoal(payload) {
    const response = await carbonApi.goal.createGoal(payload);
    const payload_data = response?.data ?? response;

    if (payload_data?.success === false) {
        const message = payload_data?.error?.message || payload_data?.message || "목표 저장에 실패했습니다.";
        const error = new Error(message);
        if (payload_data?.error?.code) {
            error.code = payload_data.error.code;
        }
        error.payload = payload_data;
        throw error;
    }

    return payload_data;
}

export async function updateGoal(targetIdx, payload) {
    if (!targetIdx) {
        throw new Error("targetIdx는 필수입니다.");
    }

    const response = await carbonApi.goal.updateGoal(targetIdx, payload);
    const payload_data = response?.data ?? response;

    if (payload_data?.success === false) {
        const message = payload_data?.error?.message || payload_data?.message || "목표 수정에 실패했습니다.";
        const error = new Error(message);
        if (payload_data?.error?.code) {
            error.code = payload_data.error.code;
        }
        error.payload = payload_data;
        throw error;
    }

    return payload_data;
}

export async function deleteGoal(targetIdx) {
    if (!targetIdx) {
        throw new Error("targetIdx는 필수입니다.");
    }

    const response = await carbonApi.goal.deleteGoal(targetIdx);
    const data = unwrap(response, { defaultMessage: "목표 삭제에 실패했습니다." });
    return {
        success: data?.success ?? true,
        message: data || "목표가 삭제되었습니다.",
    };
}

// ============================================
// 연도별 이행계획(Annual Plan) 관리 API
// ============================================

export async function fetchAnnualPlans(targetIdx = null, params = {}) {
    const response = await carbonApi.annualPlan.getAnnualPlans(targetIdx, params);
    return response;
}

export async function createAnnualPlan(targetIdx, payload) {
    if (!targetIdx) {
        throw new Error("targetIdx는 필수입니다.");
    }

    const response = await carbonApi.annualPlan.createAnnualPlan(targetIdx, payload);
    return response;
}

export async function updateAnnualPlan(targetIdx, pathwayIdx, payload) {
    if (!targetIdx || !pathwayIdx) {
        throw new Error("targetIdx와 pathwayIdx는 필수입니다.");
    }

    const response = await carbonApi.annualPlan.updateAnnualPlan(targetIdx, pathwayIdx, payload);
    return response;
}

export async function deleteAnnualPlan(targetIdx, pathwayIdx) {
    if (!targetIdx || !pathwayIdx) {
        throw new Error("targetIdx와 pathwayIdx는 필수입니다.");
    }

    const response = await carbonApi.annualPlan.deleteAnnualPlan(targetIdx, pathwayIdx);
    return response;
}

// ============================================
// 부서별 이행계획(Departmental Plan) 관리 API
// ============================================

export async function fetchDepartmentalPlans(pathwayIdx, params = {}) {
    // pathwayIdx가 없으면 전체 조회
    const response = pathwayIdx
        ? await carbonApi.departmentalPlan.getDepartmentalPlans(pathwayIdx, params)
        : await carbonApi.departmentalPlan.getDepartmentalPlans(null, params);
    const data = unwrap(response, { defaultMessage: "부서별 이행계획 목록을 불러오지 못했습니다." });

    if (Array.isArray(data?.orgTargets)) return data.orgTargets;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;

    return [];
}

export async function createDepartmentalPlan(payload) {
    // 서버는 POST /pathways/:pathwayIdx/org-targets 만 지원. pathwayIdx 필수.
    const pathwayIdx = payload.pathwayIdx ?? null;
    const response = await carbonApi.departmentalPlan.createDepartmentalPlan(pathwayIdx, payload);
    return unwrap(response, { defaultMessage: "부서별 이행계획 저장에 실패했습니다." });
}

export async function updateDepartmentalPlan(pathwayIdx, orgTargetIdx, payload) {
    if (!pathwayIdx || !orgTargetIdx) {
        throw new Error("pathwayIdx와 orgTargetIdx는 필수입니다.");
    }

    const response = await carbonApi.departmentalPlan.updateDepartmentalPlan(pathwayIdx, orgTargetIdx, payload);
    return unwrap(response, { defaultMessage: "부서별 이행계획 수정에 실패했습니다." });
}

export async function deleteDepartmentalPlan(pathwayIdx, orgTargetIdx) {
    if (!pathwayIdx || !orgTargetIdx) {
        throw new Error("pathwayIdx와 orgTargetIdx는 필수입니다.");
    }

    const response = await carbonApi.departmentalPlan.deleteDepartmentalPlan(pathwayIdx, orgTargetIdx);
    const data = unwrap(response, { defaultMessage: "부서별 이행계획 삭제에 실패했습니다." });

    return {
        success: data?.success ?? true,
        message: data?.message || "부서별 이행계획이 삭제되었습니다.",
    };
}

// ============================================
// 이행 세부항목(Implementation Action) 관리 API
// ============================================

export async function fetchImplementationActions(params = {}) {
    const response = await carbonApi.implementationAction.getActions(params);
    const data = unwrap(response, { defaultMessage: "이행 세부항목 목록을 불러오지 못했습니다." });

    if (Array.isArray(data?.orgTargetItems)) return data.orgTargetItems;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;

    return [];
}

export async function createImplementationAction(payload) {
    const response = await carbonApi.implementationAction.createAction(payload);
    return unwrap(response, { defaultMessage: "이행 세부항목 저장에 실패했습니다." });
}

export async function updateImplementationAction(itemIdx, payload) {
    if (!itemIdx) {
        throw new Error("itemIdx는 필수입니다.");
    }

    const response = await carbonApi.implementationAction.updateAction(itemIdx, payload);
    return unwrap(response, { defaultMessage: "이행 세부항목 수정에 실패했습니다." });
}

export async function deleteImplementationAction(itemIdx) {
    if (!itemIdx) {
        throw new Error("itemIdx는 필수입니다.");
    }

    const response = await carbonApi.implementationAction.deleteAction(itemIdx);
    const data = unwrap(response, { defaultMessage: "이행 세부항목 삭제에 실패했습니다." });

    return {
        success: data?.success ?? true,
        message: data?.message || "이행 세부항목이 삭제되었습니다.",
    };
}

// ============================================
// 카테고리 관리 API
// ============================================

export async function fetchCategories() {
    const response = await carbonApi.operBoundary.getAllCategories();
    const data = unwrap(response, { defaultMessage: "카테고리 목록을 불러오지 못했습니다." });

    if (Array.isArray(data?.categories)) return data.categories;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;

    return [];
}
